import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PasswordStrength } from '@/components/ui/password-strength';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

const supervisorSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain an uppercase letter")
    .regex(/[a-z]/, "Password must contain a lowercase letter")
    .regex(/\d/, "Password must contain a number")
    .regex(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain a special character"),
  agency_id: z.string().min(1, "Please select an agency"),
});

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface AddSupervisorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddSupervisorDialog({ open, onOpenChange, onSuccess }: AddSupervisorDialogProps) {
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    agency_id: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load agencies when dialog opens
  useEffect(() => {
    if (open) {
      loadAgencies();
    }
  }, [open]);

  // Pre-select user's agency if they have one
  useEffect(() => {
    if (user?.agency_id && !form.agency_id) {
      setForm(prev => ({ ...prev, agency_id: user.agency_id! }));
    }
  }, [user?.agency_id, open]);

  const loadAgencies = async () => {
    setLoadingAgencies(true);
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code')
        .eq('is_approved', true)
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error loading agencies:', error);
      toast({
        title: "Error",
        description: "Failed to load agencies",
        variant: "destructive",
      });
    } finally {
      setLoadingAgencies(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    try {
      supervisorSchema.parse(form);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
    }

    setLoading(true);

    try {
      // Create user via Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            role: 'supervisor'
          }
        }
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create user');
      }

      const userId = authData.user.id;

      // Wait a moment for the trigger to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update profile with correct agency_id (trigger creates with default agency)
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          agency_id: form.agency_id,
          role: 'agency_admin' as const // Using existing enum value
        })
        .eq('user_id', userId);

      if (profileUpdateError) {
        console.error('Profile update error:', profileUpdateError);
      }

      // Delete the auto-created patient role from trigger
      const { error: deleteRoleError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', 'patient');

      if (deleteRoleError) {
        console.error('Delete patient role error:', deleteRoleError);
      }

      // Assign supervisor role
      const { error: roleError } = await supabase
        .from('user_roles')
        .upsert({
          user_id: userId,
          role: 'supervisor',
          assigned_by: user?.id
        }, { onConflict: 'user_id,role' });

      if (roleError) {
        console.error('Role assignment error:', roleError);
        throw new Error('Failed to assign supervisor role. Please check permissions.');
      }

      toast({
        title: "Supervisor Added",
        description: `Supervisor account created for ${form.email}. They should check their email to confirm.`,
      });

      // Reset form and close
      setForm({ email: '', password: '', agency_id: user?.agency_id || '' });
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error adding supervisor:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add supervisor",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Supervisor</DialogTitle>
          <DialogDescription>
            Create a supervisor account. Supervisors can create and modify patient records.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="supervisor-agency">Agency *</Label>
              <Select
                value={form.agency_id}
                onValueChange={(value) => setForm(prev => ({ ...prev, agency_id: value }))}
                disabled={!isSuperAdmin && !!user?.agency_id}
              >
                <SelectTrigger id="supervisor-agency">
                  <SelectValue placeholder={loadingAgencies ? "Loading agencies..." : "Select an agency"} />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name} ({agency.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.agency_id && (
                <p className="text-sm text-destructive">{errors.agency_id}</p>
              )}
              {!isSuperAdmin && user?.agency_id && (
                <p className="text-xs text-muted-foreground">
                  Supervisors will be added to your agency.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="supervisor-email">Email Address *</Label>
              <Input
                id="supervisor-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="supervisor@example.com"
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="supervisor-password">Temporary Password *</Label>
              <Input
                id="supervisor-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Min 8 characters"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
              <PasswordStrength password={form.password} />
              <p className="text-xs text-muted-foreground">
                The supervisor should change this password after first login.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || loadingAgencies}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Supervisor
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}